import React from "react";
import toast from "react-hot-toast";

import { Button, Input, Modal, ModalBody, ModalFooter, ModalHeader } from "@/Components/ui";

import { membershipService } from "../../api/services/membership.service";
import {
  SiteMemberLookupResponse,
  SitePermissionLevel,
} from "../../api/types/member";

interface PermissionOption {
  value: SitePermissionLevel;
  label: string;
}

interface MemberRegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegistered: () => Promise<void>;
  permissionOptions: PermissionOption[];
  siteId?: number;
}

const normalizePhoneNumber = (value: string) => value.replace(/\D/g, "");

const MemberRegisterModal = ({
  isOpen,
  onClose,
  onRegistered,
  permissionOptions,
  siteId,
}: MemberRegisterModalProps) => {
  const [phoneNumber, setPhoneNumber] = React.useState("");
  const [selectedPermission, setSelectedPermission] = React.useState<SitePermissionLevel>(
    permissionOptions[0]?.value || 0
  );
  const [lookupResult, setLookupResult] = React.useState<SiteMemberLookupResponse | null>(null);
  const [hasSearched, setHasSearched] = React.useState(false);
  const [isLookingUp, setIsLookingUp] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [issuedSetupToken, setIssuedSetupToken] = React.useState<string | null>(null);
  const [issuedSetupUrl, setIssuedSetupUrl] = React.useState<string | null>(null);

  const resetState = React.useCallback(() => {
    setPhoneNumber("");
    setSelectedPermission(permissionOptions[0]?.value || 0);
    setLookupResult(null);
    setHasSearched(false);
    setIsLookingUp(false);
    setIsSubmitting(false);
    setIssuedSetupToken(null);
    setIssuedSetupUrl(null);
  }, [permissionOptions]);

  React.useEffect(() => {
    if (isOpen) {
      setSelectedPermission(permissionOptions[0]?.value || 0);
      return;
    }
    resetState();
  }, [isOpen, permissionOptions, resetState]);

  const handleClose = () => {
    if (isLookingUp || isSubmitting) return;
    resetState();
    onClose();
  };

  const onLookup = async () => {
    if (!siteId) {
      toast.error("사이트 정보를 찾을 수 없습니다.");
      return;
    }

    const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
    if (!normalizedPhoneNumber) {
      toast.error("전화번호를 입력해주세요.");
      return;
    }

    setPhoneNumber(normalizedPhoneNumber);
    setHasSearched(true);
    setIsLookingUp(true);
    try {
      const response = await membershipService.lookupMemberByPhone(siteId, normalizedPhoneNumber);
      setLookupResult(response.data);
    } catch (error) {
      console.log("lookupMemberByPhone error", error);
      setLookupResult(null);
      toast.error("회원 조회에 실패했습니다.");
    } finally {
      setIsLookingUp(false);
    }
  };

  const onRegister = async () => {
    if (!siteId) {
      toast.error("사이트 정보를 찾을 수 없습니다.");
      return;
    }

    const normalizedPhoneNumber = normalizePhoneNumber(phoneNumber);
    if (!normalizedPhoneNumber) {
      toast.error("전화번호를 입력해주세요.");
      return;
    }

    if (!lookupResult?.found || !lookupResult.user || lookupResult.already_member) {
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await membershipService.registerExistingMember(siteId, {
        phone_number: normalizedPhoneNumber,
        permission: selectedPermission,
      });
      await onRegistered();
      if (response.data.password_setup_required && response.data.password_setup_url) {
        setIssuedSetupToken(response.data.password_setup_token || null);
        setIssuedSetupUrl(response.data.password_setup_url);
        toast.success("멤버를 등록했고 최초 비밀번호 설정 링크를 발급했습니다.");
        return;
      }
      toast.success("멤버를 등록했습니다.");
      resetState();
      onClose();
    } catch (error) {
      console.log("registerExistingMember error", error);
      toast.error("멤버 등록에 실패했습니다.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const canRegister = Boolean(
    lookupResult?.found && lookupResult.user && !lookupResult.already_member
  );

  const onCopyIssuedSetupUrl = async () => {
    if (!issuedSetupUrl || !navigator?.clipboard?.writeText) {
      toast.error("링크를 복사할 수 없습니다.");
      return;
    }

    try {
      await navigator.clipboard.writeText(issuedSetupUrl);
      toast.success("비밀번호 설정 링크를 복사했습니다.");
    } catch (error) {
      console.log("copy password setup url error", error);
      toast.error("링크 복사에 실패했습니다.");
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      variant="dialog"
      backdrop="static"
    >
      <ModalHeader toggle={handleClose}>회원등록</ModalHeader>
      <ModalBody>
        {issuedSetupUrl ? (
          <div className="space-y-4">
            <div className="rounded border border-dashed p-3">
              <p className="mb-2">등록은 완료되었습니다.</p>
              <p className="mb-2 text-muted">
                최초 로그인 전에 아래 비밀번호 설정 링크를 전달해주세요.
              </p>
              <p className="mb-1">
                이름: {lookupResult?.user?.username || "-"}
              </p>
              <p className="mb-3">
                전화번호: {lookupResult?.user?.phone_num || phoneNumber}
              </p>
              <label htmlFor="issued-password-setup-url" className="form-label">
                비밀번호 설정 링크
              </label>
              <Input
                id="issued-password-setup-url"
                className="form-control"
                value={issuedSetupUrl}
                readOnly
              />
              {issuedSetupToken ? (
                <>
                  <label htmlFor="issued-password-setup-token" className="form-label mt-3">
                    토큰
                  </label>
                  <Input
                    id="issued-password-setup-token"
                    className="form-control"
                    value={issuedSetupToken}
                    readOnly
                  />
                </>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label htmlFor="member-register-phone-number" className="form-label">
                전화번호
              </label>
              <div className="d-flex gap-2">
                <Input
                  id="member-register-phone-number"
                  name="phoneNumber"
                  className="form-control"
                  placeholder="전화번호를 입력해주세요. ('-' 제외)"
                  value={phoneNumber}
                  onChange={(event) => setPhoneNumber(event.target.value)}
                  maxLength={13}
                  disabled={isLookingUp || isSubmitting}
                />
                <Button
                  type="button"
                  color="soft-primary"
                  onClick={onLookup}
                  disabled={isLookingUp || isSubmitting}
                >
                  {isLookingUp ? "조회 중..." : "조회"}
                </Button>
              </div>
            </div>

            <div>
              <label htmlFor="member-register-permission" className="form-label">
                권한
              </label>
              <select
                id="member-register-permission"
                name="permission"
                className="form-select"
                value={selectedPermission}
                onChange={(event) => setSelectedPermission(Number(event.target.value) as SitePermissionLevel)}
                disabled={!canRegister || isSubmitting}
              >
                {permissionOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="rounded border border-dashed p-3">
              {!hasSearched ? (
                <p className="mb-0 text-muted">전화번호를 조회하면 기존 회원 정보를 확인할 수 있습니다.</p>
              ) : null}

              {hasSearched && !isLookingUp && !lookupResult?.found ? (
                <p className="mb-0 text-danger" data-testid="member-register-not-found">
                  기본 회원을 찾을 수 없습니다.
                </p>
              ) : null}

              {lookupResult?.found && lookupResult.user ? (
                <div data-testid="member-register-result">
                  <p className="mb-1">
                    이름: {lookupResult.user.username || "-"}
                  </p>
                  <p className="mb-1">전화번호: {lookupResult.user.phone_num}</p>
                  <p className="mb-0">
                    이메일: {lookupResult.user.email || "-"}
                  </p>

                  {lookupResult.already_member ? (
                    <p className="mt-3 mb-0 text-warning" data-testid="member-register-already-member">
                      이미 등록된 멤버입니다.
                      {lookupResult.current_permission_label
                        ? ` 현재 권한: ${lookupResult.current_permission_label}`
                        : ""}
                    </p>
                  ) : (
                    <p className="mt-3 mb-0 text-muted">
                      조회된 회원을 현재 지점 관리자 멤버로 등록할 수 있습니다.
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        <Button type="button" color="soft-secondary" onClick={handleClose} disabled={isLookingUp || isSubmitting}>
          {issuedSetupUrl ? "닫기" : "취소"}
        </Button>
        {issuedSetupUrl ? (
          <Button type="button" color="success" onClick={onCopyIssuedSetupUrl}>
            링크 복사
          </Button>
        ) : (
          <Button type="button" color="success" onClick={onRegister} disabled={!canRegister || isSubmitting}>
            {isSubmitting ? "등록 중..." : "등록"}
          </Button>
        )}
      </ModalFooter>
    </Modal>
  );
};

export default MemberRegisterModal;
